import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSessionHeaders, buildEdgeFunctionHeaders } from "@/lib/session";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Loader2,
  Mail,
  Link2,
  Unlink,
  RefreshCw,
  Check,
  FileSpreadsheet,
  Clock,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import SyncTablePickerDialog from "./SyncTablePickerDialog";

interface EmailFile {
  messageId: string;
  attachmentId: string;
  fileName: string;
  mimeType: string;
  size: number | null;
  subject: string;
  from: string;
  date: string;
  alreadyApproved: boolean;
}

interface ApprovedFile {
  id: string;
  message_id: string;
  attachment_id: string;
  file_name: string;
  mime_type: string | null;
  file_size: number | null;
  email_subject: string | null;
  sync_status: string;
  error_message: string | null;
  table_name: string | null;
  created_at: string;
}

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive"; icon: React.ElementType }> = {
  pending: { label: "Pending", variant: "secondary", icon: Clock },
  synced: { label: "Synced", variant: "default", icon: CheckCircle2 },
  failed: { label: "Failed", variant: "destructive", icon: XCircle },
};

const GmailConnector = () => {
  const { user, profile, markHasData } = useAuth();
  const { toast } = useToast();

  const [connected, setConnected] = useState(false);
  const [checking, setChecking] = useState(true);
  const [emailFiles, setEmailFiles] = useState<EmailFile[]>([]);
  const [approvedFiles, setApprovedFiles] = useState<ApprovedFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [approving, setApproving] = useState(false);
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set());

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const fileKey = (f: EmailFile) => `${f.messageId}:${f.attachmentId}`;

  const checkConnection = async () => {
    setChecking(true);
    try {
      const { data, error } = await supabase.functions.invoke("gmail-auth", {
        headers: await getSessionHeaders(),
        body: { action: "check-connection" },
      });
      if (error) throw new Error(data?.error || error.message);
      setConnected(data.connected);
    } catch {
      setConnected(false);
    }
    setChecking(false);
  };

  const fetchApprovedFiles = async () => {
    const { data } = await supabase
      .from("email_files")
      .select("*")
      .order("created_at", { ascending: false });
    setApprovedFiles((data as unknown as ApprovedFile[]) || []);
  };

  useEffect(() => {
    checkConnection();
    fetchApprovedFiles();
  }, []);

  useEffect(() => {
    const handler = () => checkConnection();
    window.addEventListener("focus", handler);
    return () => window.removeEventListener("focus", handler);
  }, []);

  const handleConnect = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("gmail-auth", {
        headers: await getSessionHeaders(),
        body: { action: "get-auth-url" },
      });
      if (error) throw new Error(data?.error || error.message);
      window.open(data.url, "_blank", "width=500,height=700");
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleDisconnect = async () => {
    try {
      const { error } = await supabase.functions.invoke("gmail-auth", {
        headers: await getSessionHeaders(),
        body: { action: "disconnect" },
      });
      if (error) throw new Error(error.message);
      setConnected(false);
      setEmailFiles([]);
      toast({ title: "Disconnected", description: "Gmail disconnected." });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleScanFiles = async () => {
    setLoadingFiles(true);
    try {
      const { data, error } = await supabase.functions.invoke("gmail-list", { headers: await getSessionHeaders() });
      if (error) throw new Error(data?.error || error.message);
      setEmailFiles(data.files || []);
      setSelectedKeys(new Set());
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setLoadingFiles(false);
  };

  const toggleSelect = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectableFiles = emailFiles.filter((f) => !f.alreadyApproved);

  const toggleSelectAll = () => {
    if (selectedKeys.size === selectableFiles.length) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(selectableFiles.map(fileKey)));
    }
  };

  const syncFile = async (file: ApprovedFile) => {
    if (!file.table_name) return;
    setSyncingIds((prev) => new Set(prev).add(file.id));
    try {
      // Download attachment from Gmail via edge function using raw fetch (binary response)
      const dlHeaders = await buildEdgeFunctionHeaders();
      const dlRes = await fetch(`${supabaseUrl}/functions/v1/gmail-download`, {
        method: "POST",
        headers: dlHeaders,
        body: JSON.stringify({ messageId: file.message_id, attachmentId: file.attachment_id }),
      });

      if (!dlRes.ok) {
        const errBody = await dlRes.json().catch(() => ({ error: "Download failed" }));
        throw new Error(errBody.error || `Download failed (${dlRes.status})`);
      }

      const blob = await dlRes.blob();

      const formData = new FormData();
      formData.append("table_name", file.table_name);
      formData.append("batch_size", "1000");
      formData.append("file", blob, file.file_name);

      const fileHeaders = await buildEdgeFunctionHeaders({ contentType: null });
      const res = await fetch(`${supabaseUrl}/functions/v1/mcp-api/ingestRecords/file`, {
        method: "POST",
        headers: fileHeaders,
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Sync failed" }));
        throw new Error(err.error || err.detail || `Request failed (${res.status})`);
      }

      const result = await res.json().catch(() => ({}));

      await supabase
        .from("email_files")
        .update({
          sync_status: "synced",
          row_count: result.records_inserted ?? result.row_count ?? null,
          error_message: null,
        } as any)
        .eq("id", file.id);

      toast({ title: "Synced", description: `"${file.file_name}" synced successfully.` });
      markHasData();
    } catch (err: any) {
      await supabase
        .from("email_files")
        .update({ sync_status: "failed", error_message: err.message } as any)
        .eq("id", file.id);
      toast({ title: "Sync failed", description: err.message, variant: "destructive" });
    }
    setSyncingIds((prev) => { const next = new Set(prev); next.delete(file.id); return next; });
    await fetchApprovedFiles();
  };

  const handleApprove = async (tableName: string) => {
    if (!profile?.client_id || selectedKeys.size === 0) return;
    setApproving(true);
    try {
      const filesToApprove = emailFiles.filter((f) => selectedKeys.has(fileKey(f)));
      const rows = filesToApprove.map((f) => ({
        message_id: f.messageId,
        attachment_id: f.attachmentId,
        file_name: f.fileName,
        mime_type: f.mimeType,
        file_size: f.size,
        email_subject: f.subject,
        email_from: f.from,
        email_date: f.date ? new Date(f.date).toISOString() : null,
        sync_status: "pending",
        table_name: tableName,
        client_id: profile.client_id,
      }));

      const { data: inserted, error } = await supabase.from("email_files").insert(rows as any).select("*");
      if (error) throw new Error(error.message);

      toast({ title: "Approved", description: `${rows.length} file(s) approved. Starting sync...` });
      setSelectedKeys(new Set());
      setShowTablePicker(false);
      await Promise.all([handleScanFiles(), fetchApprovedFiles()]);

      // Sync each approved file in parallel
      if (inserted) {
        for (const row of inserted as unknown as ApprovedFile[]) {
          syncFile(row);
        }
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setApproving(false);
  };

  const formatSize = (bytes: number | null) => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatMime = (mime: string) => {
    if (mime.includes("csv")) return "CSV";
    if (mime.includes("ms-excel") || mime.includes("spreadsheetml")) return "Excel";
    return mime;
  };

  const truncate = (str: string, max: number) =>
    str.length > max ? str.slice(0, max) + "…" : str;

  if (checking) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="sticky top-0 z-10 bg-background border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Gmail</h1>
          <p className="text-sm text-muted-foreground">
            Connect your Gmail to import CSV and Excel attachments.
          </p>
        </div>
        <div className="flex gap-2">
          {connected ? (
            <>
              <Button variant="outline" onClick={handleDisconnect}>
                <Unlink className="h-4 w-4" />
                Disconnect
              </Button>
              <Button onClick={handleScanFiles} disabled={loadingFiles}>
                {loadingFiles ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Scan Emails
              </Button>
            </>
          ) : (
            <Button onClick={handleConnect}>
              <Link2 className="h-4 w-4" />
              Connect Gmail
            </Button>
          )}
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-6 p-6 overflow-y-auto">

      {!connected && (
        <Card className="flex flex-col items-center justify-center py-12">
          <Mail className="h-12 w-12 text-muted-foreground mb-4" />
          <CardTitle className="text-lg mb-1">Not Connected</CardTitle>
          <CardDescription>Connect your Gmail account to browse and import CSV/Excel attachments.</CardDescription>
        </Card>
      )}

      {connected && emailFiles.length > 0 && (
        <Card>
          <CardHeader className="pb-3 sticky top-0 z-10 bg-card border-b rounded-t-lg">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Email Attachments (CSV/Excel)</CardTitle>
              {selectableFiles.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {selectedKeys.size} of {selectableFiles.length} selected
                  </span>
                  <Button size="sm" onClick={() => setShowTablePicker(true)} disabled={selectedKeys.size === 0 || approving}>
                    <Check className="h-4 w-4" />
                    Approve Selected
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  {selectableFiles.length > 0 && (
                    <Checkbox
                      checked={selectedKeys.size === selectableFiles.length && selectableFiles.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                  )}
                </TableHead>
                <TableHead>File Name</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>From</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {emailFiles.map((file) => {
                const key = fileKey(file);
                return (
                  <TableRow key={key}>
                    <TableCell>
                      {file.alreadyApproved ? (
                        <CheckCircle2 className="h-4 w-4 text-primary" />
                      ) : (
                        <Checkbox
                          checked={selectedKeys.has(key)}
                          onCheckedChange={() => toggleSelect(key)}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="font-medium truncate max-w-[200px]">{file.fileName}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs truncate max-w-[200px]">{truncate(file.subject, 40)}</TableCell>
                    <TableCell className="text-xs truncate max-w-[150px]">{truncate(file.from, 30)}</TableCell>
                    <TableCell className="text-xs">{formatMime(file.mimeType)}</TableCell>
                    <TableCell className="text-xs">{formatSize(file.size)}</TableCell>
                    <TableCell>
                      {file.alreadyApproved ? (
                        <Badge variant="secondary" className="gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Approved
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">Not imported</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {connected && emailFiles.length === 0 && !loadingFiles && (
        <Card className="flex flex-col items-center justify-center py-12">
          <Mail className="h-12 w-12 text-muted-foreground mb-4" />
          <CardTitle className="text-lg mb-1">No Attachments Scanned</CardTitle>
          <CardDescription>Click "Scan Emails" to find CSV and Excel attachments in your inbox.</CardDescription>
        </Card>
      )}

      <SyncTablePickerDialog
        open={showTablePicker}
        onOpenChange={setShowTablePicker}
        fileCount={selectedKeys.size}
        fileNames={emailFiles.filter((f) => selectedKeys.has(fileKey(f))).map((f) => f.fileName)}
        onConfirm={handleApprove}
        confirming={approving}
      />

      {approvedFiles.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Approved Files</CardTitle>
          </CardHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File Name</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Imported</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {approvedFiles.map((file) => {
                const status = statusConfig[file.sync_status] || statusConfig.pending;
                const StatusIcon = status.icon;
                return (
                  <TableRow key={file.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="font-medium truncate max-w-[200px]">{file.file_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs truncate max-w-[200px]">{file.email_subject || "—"}</TableCell>
                    <TableCell className="text-xs">{formatMime(file.mime_type || "")}</TableCell>
                    <TableCell className="text-xs">{formatSize(file.file_size)}</TableCell>
                    <TableCell>
                      <Badge variant={status.variant} className="gap-1">
                        <StatusIcon className="h-3 w-3" />
                        {status.label}
                      </Badge>
                      {file.error_message && (
                        <div className="text-xs text-destructive mt-1">{file.error_message}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(file.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {file.sync_status !== "synced" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 h-7 text-xs"
                          disabled={syncingIds.has(file.id)}
                          onClick={() => syncFile(file)}
                        >
                          {syncingIds.has(file.id) ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                          Sync
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
      </div>
    </div>
  );
};

export default GmailConnector;
